import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, catchError, of } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';
import { ProfileService } from '../profile/profile.service';
import { environment } from '../../environments/environment';
import { extractData } from '../shared/utils/api-helpers';
import { BaseCourse, Module, Unit } from '../shared/types/course.types';

export type { Module, Unit };

// Learner-specific course interface extending shared base
export interface Course extends BaseCourse {
  unitOrder: string[];
  enrollmentStatus?: string;
  computedStatus?: string;
  computedProgress?: number;
  startedAt?: string;
  completedAt?: string;
  enrollmentDeadline?: string;
  completionDueDate?: string;
  assignedAt?: string;
  quizzesCount?: number;
  // KAN-353 time-limited access (additive fields on GET /courses/learner/:id,
  // task 5.3): expiresAt = course_access expiry (null/absent = perpetual);
  // accessExpired = paid course whose purchased access window has lapsed -
  // the course STAYS LISTED with progress retained so the learner can renew.
  // Distinct from computedStatus 'enrollment-expired' (enrollment DEADLINE).
  expiresAt?: string | null;
  accessExpired?: boolean;
}

// Progress interface
// NOTE: Backend now returns all fields in camelCase (transformed from database snake_case)
export interface Progress {
  userId: string;
  courseId: string;
  moduleId: string;
  answers: any[];
  completed: boolean;
  createdAt: string;
  updatedAt: string;
  furthestPositionSeconds?: number;
  lastPositionSeconds?: number;
  videoDurationSeconds?: number;
  videoWatchPercentage?: number;
  videoCompleted?: boolean;
  videoCompletedAt?: string;
  quizCompleted?: boolean;
  quizCompletedAt?: string;
  moduleCompleted?: boolean;
  moduleCompletedAt?: string;
  completedAt?: string;
  quizSubmissionId?: string;
  quizScore?: number;
  earnedPoints?: number | null;
  totalPoints?: number | null;
  quizPassed?: boolean;
  quizSubmittedAt?: string;
  quizGradingStatus?: string;
}

// Certificate interface
export interface Certificate {
  id: string;
  userId: string;
  courseId: string;
  userName: string;
  courseName: string;
  organizationId: string;
  issuedAt: string;
  templatePath: string | null;
  // KAN-411 / KAN-363: whether course completion REQUIRED passing the quiz
  // policy at issuance. false (or absent) => submission-only / non-gated: the
  // certificate must read "Completed", never "Passed"/"Certified".
  passingGateEnforced?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class CourseService {
  private apiUrl = environment.apiUrl;

  /**
   * Per-id cached request streams. Audit finding #5 — multiple components
   * (course player, header, sidebar) often hydrate the same course/module
   * back-to-back after a deep link or refresh. Caching via shareReplay with
   * refCount=true gives one HTTP per id while still allowing the cache to
   * clear when no subscribers remain. Mutation endpoints invalidate the
   * relevant entry so reads pick up the change.
   */
  private courseRequestCache = new Map<string, Observable<Course>>();
  private moduleRequestCache = new Map<string, Observable<Module>>();

  constructor(
    private http: HttpClient,
    private profileService: ProfileService
  ) { }

  // Get all courses assigned to the current user
  getUserCourses(): Observable<Course[]> {
    const currentUser = this.profileService.getCurrentUser();
    if (!currentUser) {
      throw new Error('User not authenticated');
    }
    
    return this.http.get<{success: boolean, data: Course[]}>(`${this.apiUrl}/courses/learner/${currentUser.id}`)
      .pipe(
        extractData([])
      );
  }

  // Get a specific course by ID — cached per-id via shareReplay.
  getCourse(courseId: string): Observable<Course> {
    const cached = this.courseRequestCache.get(courseId);
    if (cached) {
      return cached;
    }
    const stream = this.http
      .get<{success: boolean, data: Course}>(`${this.apiUrl}/courses/${courseId}`)
      .pipe(
        extractData(),
        shareReplay({ bufferSize: 1, refCount: true })
      );
    this.courseRequestCache.set(courseId, stream);
    return stream;
  }

  // Get a specific unit by ID
  getUnit(unitId: string): Observable<Unit> {
    return this.http.get<Unit>(`${this.apiUrl}/units/${unitId}`);
  }

  // Get a specific module by ID — cached per-id via shareReplay.
  getModule(moduleId: string): Observable<Module> {
    const cached = this.moduleRequestCache.get(moduleId);
    if (cached) {
      return cached;
    }
    const stream = this.http
      .get<Module>(`${this.apiUrl}/modules/${moduleId}`)
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));
    this.moduleRequestCache.set(moduleId, stream);
    return stream;
  }

  // Get progress for a user on a specific course
  getCourseProgress(courseId: string): Observable<Progress[]> {
    const currentUser = this.profileService.getCurrentUser();
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    return this.http.get<{success: boolean, data: Progress[]}>(
      `${this.apiUrl}/progress/user/${currentUser.id}/course/${courseId}`
    ).pipe(
      map(response => {
        // Backend returns { success: true, data: Progress[] } as a flat array
        if (response && response.data && Array.isArray(response.data)) {
          return response.data;
        }
        return [];
      }),
      catchError(error => {
        console.error('Error fetching course progress:', error);
        // Return empty array on error
        return of([]);
      })
    );
  }

  // Get progress for a user on a specific module
  getModuleProgress(moduleId: string): Observable<Progress> {
    const currentUser = this.profileService.getCurrentUser();
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    return this.http.get<any>(
      `${this.apiUrl}/progress/user/${currentUser.id}/module/${moduleId}`
    ).pipe(
      map(response => {
        // Handle wrapped response format { success: true, data: {...} }
        if (response && typeof response === 'object' && 'success' in response && 'data' in response) {
          return response.data;
        }

        // If it's already the progress object, return it directly
        return response;
      })
    );
  }

  // Get certificates for the current user
  getUserCertificates(): Observable<Certificate[]> {
    const currentUser = this.profileService.getCurrentUser();
    if (!currentUser) {
      throw new Error('User not authenticated');
    }
    
    return this.http.get<{success: boolean, data: any[]}>(
      `${this.apiUrl}/progress/certificates/learner/${currentUser.id}`
    ).pipe(
      extractData<any[]>([]),
      // KAN-411: the list route returns camelized DB rows (certificateId /
      // courseTitle / ...). The "My Certificates" cards and the View link read
      // the Certificate shape (id / courseName / passingGateEnforced), so map
      // explicitly here - previously only issuedAt lined up, leaving blank names
      // and a /certificates/undefined link.
      map((rows: any[]) => (rows || []).map((r) => ({
        id: r.certificateId,
        userId: r.userId ?? '',
        courseId: r.courseId,
        userName: r.certificateData?.userName ?? '',
        courseName: r.courseTitle ?? r.certificateData?.courseTitle ?? '',
        organizationId: r.organizationId ?? '',
        issuedAt: r.issuedAt,
        templatePath: null,
        passingGateEnforced: r.passingGateEnforced === true,
      } as Certificate)))
    );
  }

  // Get course content (units and direct modules)
  getCourseContent(courseId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/courses/${courseId}/content`);
  }

  // Get a specific certificate
  getCertificate(certificateId: string): Observable<Certificate> {
    // KAN-411: the API wraps payloads as { success, data }, so unwrap to the
    // Certificate the viewer binds to (it reads .userName/.courseName/
    // .passingGateEnforced directly). Without this the viewer got the envelope
    // and rendered blank fields.
    return this.http.get<{ success: boolean; data: Certificate }>(
      `${this.apiUrl}/progress/certificates/${certificateId}`
    ).pipe(
      extractData<Certificate>()
    );
  }

  // Start a course (update enrollment status) — invalidates per-id cache.
  startCourse(courseId: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/courses/${courseId}/start`, {})
      .pipe(tap(() => this.courseRequestCache.delete(courseId)));
  }

  // Learner self-reset — invalidates per-id cache so next read is fresh.
  resetOwnProgress(courseId: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/courses/${courseId}/self-reset`, {})
      .pipe(tap(() => this.courseRequestCache.delete(courseId)));
  }
} 